import type { ListDealBase, OAListDeal } from '@openx/types/deal';
import { useCallback } from 'react';

import { useDealContext } from '../context/DealContext';
import { type DealHealthState, getHealthScoreStateFromScore, getHealthScoreValue } from '../healthScoreUtils';

export type MockHealthScoreState = {
  aiAnalysis: string | null;
  currentScore: number | null;
  error: boolean;
  healthState: DealHealthState;
  loading: boolean;
  score: number | null;
};

const mockHealthScores: (number | null)[] = [null, 0, 5, 12, 19, 20, 33, 45, 50, 65, 69, 70, 80, 90, 100];

const getRootCauseDealId = (deal: ListDealBase): string => {
  // OA/OXS list rows keep the OA UUID in `id` and the OpenX integer ID in `exchange_id`.
  // WISSP root-cause requires the OpenX integer ID.
  const exchangeId = (deal as OAListDeal).exchange_id;
  return exchangeId || deal.id || '';
};

export const useDealHealthScoreMock = () => {
  const { useDealHealthScoreRootCauseDetails } = useDealContext();
  const { getDealRootCauseDetails, loadDealRootCauseDetails, reloadDealRootCauseDetails } =
    useDealHealthScoreRootCauseDetails();

  const getDealKey = useCallback((deal: ListDealBase): string => {
    return getRootCauseDealId(deal);
  }, []);

  const getBaseHealthScoreForDeal = useCallback((deal: ListDealBase): MockHealthScoreState => {
    if ('health_score' in deal && deal.health_score !== undefined) {
      return {
        ...getHealthScoreValue(deal.health_score),
        currentScore: null,
      };
    }

    const source = deal.deal_id ?? deal.uid ?? deal.name ?? '';
    const hash = [...source].reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const score = mockHealthScores[hash % mockHealthScores.length];

    return {
      aiAnalysis: null,
      currentScore: null,
      error: false,
      healthState: getHealthScoreStateFromScore(score),
      loading: false,
      score,
    };
  }, []);

  const getHealthScoreForDeal = useCallback(
    (deal: ListDealBase): MockHealthScoreState => {
      const baseHealthScore = getBaseHealthScoreForDeal(deal);
      const dealKey = getDealKey(deal);
      const details = dealKey ? getDealRootCauseDetails(dealKey) : undefined;

      return {
        ...baseHealthScore,
        ...(details ?? { aiAnalysis: null, currentScore: null, error: false, loading: false }),
      };
    },
    [getBaseHealthScoreForDeal, getDealKey, getDealRootCauseDetails]
  );

  const loadHealthScoreDetails = useCallback(
    (deal: ListDealBase, forceSuccess = false) => {
      const dealKey = getDealKey(deal);
      const baseHealthScore = getBaseHealthScoreForDeal(deal);

      if (!dealKey || baseHealthScore.healthState === 'na') {
        return;
      }

      void loadDealRootCauseDetails(dealKey, { force: forceSuccess });
    },
    [getBaseHealthScoreForDeal, getDealKey, loadDealRootCauseDetails]
  );

  const reloadHealthScore = useCallback(
    (deal: ListDealBase) => {
      const dealKey = getDealKey(deal);

      if (!dealKey) {
        return;
      }

      void reloadDealRootCauseDetails(dealKey);
    },
    [getDealKey, reloadDealRootCauseDetails]
  );

  return {
    getHealthScoreForDeal,
    loadHealthScoreDetails,
    reloadHealthScore,
  };
};
